import { STATUS_LABELS, STATUS_CLASSES } from "./phone_statuses.config";

import { checkPhonesStatus, bulkUpdatePhoneStatus } from "./api";

import { updatePhoneStatusUI } from "./services/status.service";

import { showCheckResultModal, showBulkStatusModal, showConnectModal } from "./services/modal.service";

/** Константы с CSS-селекторами элементов интерфейса */
const SELECTORS = {
  /** Чекбокс выбора всех номеров */
  selectAll: "#select-all-phones",
  /** Чекбоксы отдельных номеров */
  phoneCheckbox: ".phone-checkbox",
  /** Кнопка смены статуса номеров */
  bulkStatusButton: "#bulk-change-status-btn",
  /** Кнопка подключения номеров */
  bulkConnectButton: "#bulk-connect-btn",
  /** Ячейка таблицы с номером телефона */
  phoneColumn: '[data-column-name="phone_number"]',
  /** Ячейка таблицы с оператором */
  operatorColumn: '[data-column-name="operator"]',
  /** Бейдж статуса номера */
  statusBadge: ".status-badge-ready",
};

/** Тексты кнопок */
const BUTTON_TEXTS = {
  /** Текст кнопки смены статуса */
  bulkStatus: "Сменить статус",
  /** Текст кнопки подключения */
  bulkConnect: "Подключить выбранные",
};

/** Класс кнопок действий таблицы (чекбоксы, кнопки "Подключить выбранные" и "Сменить статус") */
export default class BulkPhoneActions {
  constructor() {
    this.selectedPhones = new Map();

    this.handleChange = this.handleChange.bind(this);
    this.handleFilterReset = this.handleFilterReset.bind(this);
    this.handleBulkStatusClick = this.handleBulkStatusClick.bind(this);
    this.handleBulkConnectClick = this.handleBulkConnectClick.bind(this);
  }

  init() {
    document.addEventListener("change", this.handleChange);

    window.addEventListener("table:beforeFilter", this.handleFilterReset);

    this.getButton(SELECTORS.bulkStatusButton)?.addEventListener("click", this.handleBulkStatusClick);

    this.getButton(SELECTORS.bulkConnectButton)?.addEventListener("click", this.handleBulkConnectClick);
  }

  destroy() {
    document.removeEventListener("change", this.handleChange);

    window.removeEventListener("table:beforeFilter", this.handleFilterReset);

    this.getButton(SELECTORS.bulkStatusButton)?.removeEventListener("click", this.handleBulkStatusClick);

    this.getButton(SELECTORS.bulkConnectButton)?.removeEventListener("click", this.handleBulkConnectClick);
  }

  /** Добавляет номер в список выбранных
   * @param {Object} phone - Данные номера
   * @returns {void}
   */
  addPhone(phone) {
    this.selectedPhones.set(phone.id, phone);
  }

  /** Удаляет номер из списка выбранных
   * @param {number} id - ID номера
   * @returns {void}
   */
  removePhone(id) {
    this.selectedPhones.delete(id);
  }

  /** Проверяет, выбран ли номер
   * @param {number} id - ID номера
   * @returns {boolean}
   */
  hasPhone(id) {
    return this.selectedPhones.has(id);
  }

  /** Возвращает ID всех выбранных номеров
   * @returns {number[]}
   */
  getSelectedIds() {
    return [...this.selectedPhones.keys()];
  }

  /** Возвращает данные всех выбранных номеров
   * @returns {Object[]}
   */
  getSelectedItems() {
    return [...this.selectedPhones.values()];
  }

  /** Возвращает количество выбранных номеров
   * @returns {number}
   */
  getSelectedCount() {
    return this.selectedPhones.size;
  }

  /** Возвращает список номеров для подключения
   * @returns {Object[]}
   */
  getSelectedPhonesForConnect() {
    return this.getSelectedItems().map(({ id, label, operator }) => ({
      id,
      label,
      operator,
    }));
  }

  /** Получает данные номера из строки таблицы
   * @param {HTMLTableRowElement} row - Строка таблицы
   * @param {HTMLInputElement} checkbox - Чекбокс номера
   * @returns {Object}
   */
  getPhoneDataFromRow(row, checkbox) {
    return {
      id: Number(checkbox.value),
      label: row?.querySelector(SELECTORS.phoneColumn)?.textContent.trim(),
      operator: row?.querySelector(SELECTORS.operatorColumn)?.textContent.trim(),
      status: row?.querySelector(SELECTORS.statusBadge)?.textContent.trim(),
      row,
    };
  }

  /** Возвращает все чекбоксы номеров
   * @returns {NodeListOf<HTMLInputElement>}
   */
  getCheckboxes() {
    return document.querySelectorAll(SELECTORS.phoneCheckbox);
  }

  /** Возвращает DOM-элемент кнопки по селектору
   * @param {string} selector - CSS-селектор кнопки
   * @returns {HTMLElement | null}
   */
  getButton(selector) {
    return document.querySelector(selector);
  }

  /** Добавление обработчика на кнопку "Сменить статус"
   * @returns {void}
   */
  setupBulkStatusButton() {
    this.getButton(SELECTORS.bulkStatusButton)?.addEventListener("click", this.handleBulkStatusClick);
  }

  /** Обработчик клика по кнопке "Сменить статус"
   * @returns {void}
   */
  async handleBulkStatusClick() {
    const status = await showBulkStatusModal(this.getSelectedCount());

    if (!status) return;

    this.checkStatusBeforeUpdate(status);
  }

  /** Обработчик клика по кнопке "Подключить выбранные"
   * @returns {void}
   */
  async handleBulkConnectClick() {
    const success = await showConnectModal(this.getSelectedPhonesForConnect(), this.getSelectedCount());

    if (!success) return;

    this.resetSelection();

    toastr.success("Номера успешно подключены", "success");
  }

  /** Обработчик клика по чекбоксам таблицы
   * @returns {void}
   */
  handleChange(event) {
    if (event.target.id === "select-all-phones") {
      const checked = event.target.checked;

      this.getCheckboxes().forEach(checkbox => {
        checkbox.checked = checked;

        this.togglePhoneSelection(checkbox, checked);
      });

      this.updateBulkActionButtons();

      return;
    }

    if (event.target.classList.contains("phone-checkbox")) {
      this.togglePhoneSelection(event.target, event.target.checked);

      this.syncSelectAllState();
      this.updateBulkActionButtons();
    }
  }

  /** Переключает состояние выбора номера
   * @param {HTMLInputElement} checkbox - Чекбокс номера
   * @param {boolean} checked - Состояние выбора
   * @returns {void}
   */
  togglePhoneSelection(checkbox, checked) {
    const row = checkbox.closest("tr");
    const phone = this.getPhoneDataFromRow(row, checkbox);

    if (checked) {
      this.addPhone(phone);
    } else {
      this.removePhone(phone.id);
    }
  }

  /** Сбрасывает все выбранные чекбоксы
   * @returns {void}
   */
  resetSelection() {
    this.selectedPhones.clear();

    this.getCheckboxes().forEach(checkbox => {
      checkbox.checked = false;
    });

    const selectAll = this.getButton(SELECTORS.selectAll);

    if (selectAll) {
      selectAll.checked = false;
    }

    this.updateBulkActionButtons();
  }

  /** Сброс выбранных номеров при фильтрации таблицы
   * @returns {void}
   */
  handleFilterReset() {
    this.resetSelection();
  }

  /** Синхронизирует состояние чекбокса "выбрать все"
   * @returns {void}
   */
  syncSelectAllState() {
    const selectAll = this.getButton(SELECTORS.selectAll);

    if (!selectAll) return;

    const checkboxes = [...this.getCheckboxes()];
    const checkedCount = checkboxes.filter(checkbox => checkbox.checked).length;

    selectAll.checked = Boolean(checkboxes.length) && checkedCount === checkboxes.length;
  }

  /** Инициализирует обработчик кнопки массового подключения
   * @returns {void}
   */
  setupBulkConnectButton() {
    this.getButton(SELECTORS.bulkConnectButton)?.addEventListener("click", async () => {
      const success = await showConnectModal(this.getSelectedPhonesForConnect(), this.getSelectedCount());

      if (!success) return;

      this.resetSelection();

      toastr.success("Номера успешно подключены", "success");
    });
  }

  /** Проверяет возможность смены статуса перед обновлением
   * @param {string} status - Новый статус
   * @returns {Promise<void>}
   */
  async checkStatusBeforeUpdate(status) {
    try {
      const data = await checkPhonesStatus(this.getSelectedIds(), status);
      const confirmed = await showCheckResultModal(data);

      if (!confirmed) {
        toastr.warning("Изменение отменено", "warning");

        return;
      }

      await this.bulkUpdateStatus(status);
    } catch (error) {
      console.error(error);

      toastr.error("Ошибка при проверке номеров", "error");
    }
  }

  /** Обновляет статус выбранных номеров
   * @param {string} status - Новый статус
   * @returns {Promise<void>}
   */
  async bulkUpdateStatus(status) {
    try {
      const phoneIds = this.getSelectedIds();
      const data = await bulkUpdatePhoneStatus(phoneIds, status);

      if (!data.success) {
        toastr.error(data.message || "Ошибка при изменении статуса", "error");

        return;
      }

      this.selectedPhones.forEach(({ id }) => {
        updatePhoneStatusUI(id, status, data.reserved_for_email);
      });

      this.resetSelection();

      toastr.success(`Статус изменён для ${data.updated_count} номеров`, "success");
    } catch (error) {
      console.error(error);

      toastr.error("Произошла ошибка", "error");
    }
  }

  /** Проверяет, можно ли подключить выбранные номера
   * @returns {boolean}
   */
  canConnectSelectedPhones() {
    return (
      this.getSelectedCount() > 0 &&
      this.getSelectedItems().every(item => item.status === STATUS_LABELS["ready_for_connection"])
    );
  }

  /** Обновляет состояние кнопки
   * @param {HTMLButtonElement} button - Кнопка
   * @param {Object} options - Настройки кнопки
   * @param {boolean} options.disabled - Состояние disabled
   * @param {string} options.text - Текст кнопки
   * @returns {void}
   */
  updateButtonState(button, { disabled, text }) {
    if (!button) return;

    button.disabled = disabled;
    button.textContent = text;
  }

  /** Обновляет состояние кнопок действий
   * @returns {void}
   */
  updateBulkActionButtons() {
    const count = this.getSelectedCount();
    const bulkStatusBtn = this.getButton(SELECTORS.bulkStatusButton);
    const bulkConnectBtn = this.getButton(SELECTORS.bulkConnectButton);

    this.updateButtonState(bulkStatusBtn, {
      disabled: count === 0,

      text: count ? `${BUTTON_TEXTS.bulkStatus} (${count})` : BUTTON_TEXTS.bulkStatus,
    });

    this.updateButtonState(bulkConnectBtn, {
      disabled: !this.canConnectSelectedPhones(),

      text: count ? `${BUTTON_TEXTS.bulkConnect} (${count})` : BUTTON_TEXTS.bulkConnect,
    });
  }
}
